import { forwardRef, RefObject } from 'react';

import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Portal,
  Stack,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
  Checkbox,
} from '@chakra-ui/react';

import { format, parseISO } from 'date-fns';

import { useQueryClient } from '@tanstack/react-query';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  CalendarOutline,
  CardStatus,
  CardStatusChangedReason,
  CheckOutline,
  CompanyConfiguration,
  CrossOutline,
  DEFAULT_DATE_FORMAT,
  IndividualRole,
  InfoCircleOutline,
  useToast,
  LockOutline,
  UnlockOutline,
} from '@aslanpay/portal-common-lib';

import EditEmployeeTerminationDateModal from 'app/components/Employees/EditEmployeeTerminationDate/EditEmployeeTerminationDateModal';
import PreviousEmploymentSection from 'app/components/Individuals/IndividualProfile/PreviousEmploymentSection';
import EditSalaryAllowanceModal from 'app/components/Employees/EditSalaryAllowanceModal';
import ManageUnpaidLeaveModal from 'app/components/Employees/ManageUnpaidLeaveModal';
import ManageCardLockModal from 'app/components/Employees/ManageCardLockModal';
import TerminateEmployeeModal from 'app/components/Employees/TerminateEmployee/TerminateEmployeeModal';
import PhoneNumberInput from 'app/components/UI/Inputs/PhoneNumberInput';
import ProfileField from 'app/components/UI/ProfileField';
import ProfileSection from 'app/components/UI/ProfileSection';

import { CardStatusValues, CardStatusChangedReasonValues } from 'app/constants/cards';
import { Role } from 'app/constants/individual';

import useUpdateIndividual from 'app/hooks/useUpdateIndividual';

import {
  getAdminProfilePath,
  getEmployeeProfilePath,
  getSelectedCompanyPath,
} from 'app/utils/routes';

import { individualProfileFormSchema } from 'app/utils/schemas';

import getTribeAdminLink from 'app/utils/tribe';

import {
  MODULE_DISPLAY_INFO,
  formatModuleName,
} from 'app/components/Companies/CreateCompany/CreateCompanyForm/types';

import { IndividualProfileFormFields, IndividualProfileTabProps } from './types';

const IndividualProfileTab = forwardRef<HTMLDivElement, IndividualProfileTabProps>(
  (
    {
      isEmployee,
      individual,
      company,
      payCalendar,
      wallet,
      card,
      editMode,
      onCloseEditMode,
      individualConfiguration,
      companyConfiguration,
    },
    ref
  ) => {
    const queryClient = useQueryClient();

    const toast = useToast();

    const {
      isOpen: isTerminateOpen,
      onOpen: onOpenTerminate,
      onClose: onCloseTerminate,
    } = useDisclosure();

    const {
      isOpen: isEditTerminationDateOpen,
      onOpen: onOpenEditTerminationDate,
      onClose: onCloseEditTerminationDate,
    } = useDisclosure();

    const {
      isOpen: isManageUnpaidLeaveOpen,
      onOpen: onOpenManageUnpaidLeave,
      onClose: onCloseManageUnpaidLeave,
    } = useDisclosure();

    const {
      isOpen: isEditSalaryAllowanceOpen,
      onOpen: onOpenEditSalaryAllowance,
      onClose: onCloseEditSalaryAllowance,
    } = useDisclosure();

    const {
      isOpen: isManageCardLockOpen,
      onOpen: onOpenManageCardLock,
      onClose: onCloseManageCardLock,
    } = useDisclosure();

    const {
      register,
      control,
      handleSubmit,
      watch,
      reset,
      setFocus,
      setValue,
      formState: { errors, isValid, isDirty },
    } = useForm<IndividualProfileFormFields>({
      mode: 'onBlur',
      defaultValues: {
        firstName: individual?.firstName || '',
        lastName: individual?.lastName || '',
        email: individual?.email || '',
        phoneNumber: individual?.phoneNumber || '',
        canTopUpCompanyPoints: individual?.canTopUpCompanyPoints ?? false,
      },
      resolver: yupResolver(individualProfileFormSchema),
    });

    const refreshIndividual = () => {
      queryClient.invalidateQueries({ queryKey: ['fetchIndividual', individual?.id] });
    };

    const refreshCard = () => {
      queryClient.invalidateQueries({ queryKey: ['fetchCards'] });
    };

    const { mutate: updateIndividual, isLoading: isUpdatingIndividual } = useUpdateIndividual({
      onSuccess: () => {
        toast({
          position: 'top-right',
          status: 'success',
          title: 'Profile details saved',
          description: 'The user details were successfully saved!',
        });

        refreshIndividual();

        onCloseEditMode();
      },
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: 'It was not possible to save the user details.',
        });
      },
    });

    const handleCancelUpdate = () => {
      reset({
        firstName: individual?.firstName,
        lastName: individual?.lastName,
        email: individual?.email,
        phoneNumber: individual?.phoneNumber,
        canTopUpCompanyPoints: individual?.canTopUpCompanyPoints,
      });

      onCloseEditMode();
    };

    const phoneNumberIsDisabled =
      individual?.status === 'CREATED' || individual?.status === 'INVITED';

    const handleUpdateIndividual = (data: IndividualProfileFormFields) => {
      // TODO: need to revisit the logic about profiles to guarantee that individual exists
      if (individual) {
        updateIndividual({
          ...individual,
          id: individual?.id || '',
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          ...(phoneNumberIsDisabled ? {} : { phoneNumber: data.phoneNumber }),
          canTopUpCompanyPoints: data.canTopUpCompanyPoints,
        });

        reset(data);
      }
    };

    const resetAndFocusField = (field: keyof IndividualProfileFormFields) => {
      setValue(field, '', { shouldValidate: true, shouldDirty: true });
      setFocus(field);
    };

    const canTopUpCompanyPointsIsDisabled = false;

    const watchFirstName = watch('firstName');
    const watchLastName = watch('lastName');
    const watchEmail = watch('email');

    const renderModals = () => (
      <>
        <TerminateEmployeeModal
          id={individual?.id || ''}
          isOpen={isTerminateOpen}
          onClose={onCloseTerminate}
          onTerminate={refreshIndividual}
        />
        <EditEmployeeTerminationDateModal
          id={individual?.id || ''}
          isOpen={isEditTerminationDateOpen}
          onClose={onCloseEditTerminationDate}
          onEditTerminate={refreshIndividual}
        />
        <ManageUnpaidLeaveModal
          id={individual?.id || ''}
          isOpen={isManageUnpaidLeaveOpen}
          onClose={onCloseManageUnpaidLeave}
          onUnpaidLeave={refreshIndividual}
        />
        <EditSalaryAllowanceModal
          id={individual?.id || ''}
          isOpen={isEditSalaryAllowanceOpen}
          onClose={onCloseEditSalaryAllowance}
          onEdit={refreshIndividual}
        />
        <ManageCardLockModal
          id={card?.id || ''}
          employeeName={`${individual?.firstName} ${individual?.lastName}`}
          isOpen={isManageCardLockOpen}
          onClose={onCloseManageCardLock}
          onManageCardLock={refreshCard}
        />
      </>
    );

    const renderGeneralDetails = () => (
      <GridItem display="flex">
        <ProfileSection title="General details" status={individual?.status} isEditMode={editMode}>
          {editMode ? (
            <form
              id="edit-individual-profile"
              onSubmit={handleSubmit(handleUpdateIndividual)}
              noValidate
            >
              <Stack spacing="20px">
                <HStack spacing="20px" alignItems="flex-start">
                  <FormControl isInvalid={!!errors?.firstName?.message} isRequired>
                    <FormLabel>First name</FormLabel>
                    <InputGroup>
                      <Input
                        type="text"
                        placeholder="Enter first name"
                        {...register('firstName')}
                      />
                      {watchFirstName && (
                        <InputRightElement
                          h="full"
                          cursor="pointer"
                          onClick={() => resetAndFocusField('firstName')}
                        >
                          <CrossOutline color="brand.darkGray" boxSize="24px" />
                        </InputRightElement>
                      )}
                    </InputGroup>
                    <FormErrorMessage>{errors?.firstName?.message}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!errors?.lastName?.message} isRequired>
                    <FormLabel>Last name</FormLabel>
                    <InputGroup>
                      <Input type="text" placeholder="Enter last name" {...register('lastName')} />
                      {watchLastName && (
                        <InputRightElement
                          h="full"
                          cursor="pointer"
                          onClick={() => resetAndFocusField('lastName')}
                        >
                          <CrossOutline color="brand.darkGray" boxSize="24px" />
                        </InputRightElement>
                      )}
                    </InputGroup>
                    <FormErrorMessage>{errors?.lastName?.message}</FormErrorMessage>
                  </FormControl>
                </HStack>
                <FormControl isInvalid={!!errors?.email?.message} isRequired>
                  <FormLabel>Email</FormLabel>
                  <InputGroup>
                    <Input type="email" placeholder="Enter your email" {...register('email')} />
                    {watchEmail && (
                      <InputRightElement
                        h="full"
                        cursor="pointer"
                        onClick={() => resetAndFocusField('email')}
                      >
                        <CrossOutline color="brand.darkGray" boxSize="24px" />
                      </InputRightElement>
                    )}
                  </InputGroup>
                  <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={!!errors?.phoneNumber?.message}
                  isRequired={individual?.status === 'ACTIVE'}
                  isDisabled={phoneNumberIsDisabled}
                >
                  <Flex align="flex-start" gap="-6px">
                    <FormLabel mr="6px">Phone number</FormLabel>
                    <Tooltip
                      hasArrow
                      shouldWrapChildren
                      arrowPadding={0}
                      placement="top"
                      label="You cannot edit one's phone number while in the Created or Invited status. Users will only introduce their phone number during Sign Up."
                    >
                      <Flex mt="1px">
                        <InfoCircleOutline
                          boxSize="15px"
                          color="brand.darkPurple"
                          cursor="pointer"
                        />
                      </Flex>
                    </Tooltip>
                  </Flex>
                  <Controller
                    control={control}
                    name="phoneNumber"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <PhoneNumberInput
                        defaultValue={value}
                        isDisabled={phoneNumberIsDisabled}
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  <FormErrorMessage>{errors?.phoneNumber?.message}</FormErrorMessage>
                </FormControl>
                {!isEmployee && individual?.role === 'COMPANY_ADMIN' && (
                  <FormControl isDisabled={canTopUpCompanyPointsIsDisabled}>
                    <Flex align="flex-start" gap="-6px">
                      <FormLabel mr="6px">Can top up company points</FormLabel>
                      <Tooltip
                        hasArrow
                        shouldWrapChildren
                        arrowPadding={0}
                        placement="top"
                        label="Can top up company points means this user is authorised to purchase top up points on behalf of the company. An invoice will be issued for each top up points request."
                      >
                        <Flex mt="1px">
                          <InfoCircleOutline
                            boxSize="15px"
                            color="brand.darkPurple"
                            cursor="pointer"
                          />
                        </Flex>
                      </Tooltip>
                    </Flex>
                    <Controller
                      control={control}
                      name="canTopUpCompanyPoints"
                      render={({ field: { onChange, value } }) => (
                        <Checkbox
                          size="md"
                          isChecked={value}
                          onChange={onChange}
                          isDisabled={canTopUpCompanyPointsIsDisabled}
                        >
                          Allow this admin to top up company points
                        </Checkbox>
                      )}
                    />
                  </FormControl>
                )}
              </Stack>
              <Portal containerRef={ref as RefObject<HTMLElement>}>
                <Flex gap="20px" alignItems="flex-start">
                  <Button variant="outline" size="sm" onClick={handleCancelUpdate}>
                    Cancel
                  </Button>
                  <Button
                    leftIcon={<CheckOutline boxSize="20px" />}
                    size="sm"
                    type="submit"
                    form="edit-individual-profile"
                    isLoading={isUpdatingIndividual}
                    isDisabled={!isValid || !isDirty}
                  >
                    Save changes
                  </Button>
                </Flex>
              </Portal>
            </form>
          ) : (
            <>
              <HStack spacing="20px" align="flex-start">
                <ProfileField label="First name" value={individual?.firstName} />
                <ProfileField label="Last name" value={individual?.lastName} />
              </HStack>
              <HStack spacing="20px" align="flex-start">
                <ProfileField label="Email" value={individual?.email} />
                <ProfileField label="Phone number" value={individual?.phoneNumber} />
              </HStack>
              <HStack spacing="20px" align="flex-start">
                <ProfileField
                  label="Company"
                  value={company?.name}
                  link={company && getSelectedCompanyPath(company?.id)}
                />
                {!isEmployee && (
                  <ProfileField label="Role" value={Role[individual?.role as IndividualRole]} />
                )}
              </HStack>
              {!isEmployee && individual?.role === 'COMPANY_ADMIN' && (
                <HStack spacing="20px" align="flex-start">
                  <ProfileField
                    label="Can top up company points"
                    value={individual?.canTopUpCompanyPoints ? 'Yes' : 'No'}
                  />
                </HStack>
              )}
            </>
          )}
        </ProfileSection>
      </GridItem>
    );

    const renderModuleDetails = () => (
      <GridItem display="flex" colSpan={{ base: 1, md: 2 }}>
        <ProfileSection title="Module details" maxW="100%">
          <Flex gap="20px" flexWrap="wrap" align="flex-start">
            {Object.entries(individualConfiguration?.modulesConfig.modules || {})
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([key, moduleOptions]) => (
                <Box key={key}>
                  <Text mb="15px" color="brand.black" textStyle="extraSmallRegular">
                    {MODULE_DISPLAY_INFO[key]?.title ?? formatModuleName(key)}
                  </Text>
                  <Badge
                    key={key}
                    colorScheme={`badge.${moduleOptions === 'ENABLED' ? 'active' : 'unpaid'}`}
                  >
                    {moduleOptions === 'ENABLED' ? 'Active' : 'Disabled'}
                  </Badge>
                </Box>
              ))}
          </Flex>
        </ProfileSection>
      </GridItem>
    );

    const renderEmployeeDetails = (companyConfig?: CompanyConfiguration) => (
      <GridItem display="flex">
        <ProfileSection title="Employee details">
          {companyConfig?.modulesConfig?.modules?.RESERVE === 'ENABLED' && (
            <Box>
              <Text color="brand.black" textStyle="extraSmallRegular">
                Global Limit
              </Text>
              <Flex w="full" alignItems="center" py="15px">
                <Text color="brand.black" textStyle="smallMedium" wordBreak="break-word">
                  {individual?.globalAllowancePercentage !== undefined
                    ? `${individual?.globalAllowancePercentage}% (individual setting)`
                    : `${payCalendar?.globalAllowancePercentage || 0}% (pay calendar default)`}
                </Text>

                <Button
                  variant="link"
                  size="sm"
                  ml="auto"
                  onClick={onOpenEditSalaryAllowance}
                  isDisabled={individual?.status === 'LEFT'}
                >
                  Edit % allowance
                </Button>
              </Flex>
            </Box>
          )}

          {companyConfig?.modulesConfig?.modules?.ON_DEMAND_PAY === 'ENABLED' && (
            <Box>
              <Text color="brand.black" textStyle="extraSmallRegular">
                ODP Limit
              </Text>
              <Flex w="full" alignItems="center" py="15px">
                <Text color="brand.black" textStyle="smallMedium" wordBreak="break-word">
                  {individual && individual.odpAllowancePercentage !== undefined
                    ? `${individual?.odpAllowancePercentage}% (individual setting)`
                    : `${payCalendar?.odpAllowancePercentage || 0}% (pay calendar default)`}
                </Text>

                <Button
                  variant="link"
                  size="sm"
                  ml="auto"
                  onClick={onOpenEditSalaryAllowance}
                  isDisabled={individual?.status === 'LEFT'}
                >
                  Edit % allowance
                </Button>
              </Flex>
            </Box>
          )}

          <Flex gap="5px" flexDir="column" w="full">
            <Text color="brand.black" textStyle="extraSmallRegular">
              Accrual status
            </Text>
            <Flex w="full" alignItems="center" py="15px">
              <Badge colorScheme={`badge.${individual?.onUnpaidLeave ? 'unpaid' : 'active'}`}>
                {individual?.onUnpaidLeave ? 'Accrual paused' : 'Accrual active'}
              </Badge>
              <Flex ml="auto" gap="15px" align="center">
                <Text textStyle="smallMedium" color="brand.purple">
                  {individual?.onUnpaidLeave ? 'On' : 'Off'}
                </Text>
                <Switch
                  colorScheme="badge.invited"
                  ml="auto"
                  size="lg"
                  isChecked={individual?.onUnpaidLeave}
                  isDisabled={
                    individual?.status &&
                    !['CREATED', 'INVITED', 'ACTIVE', 'LEAVING'].includes(individual?.status)
                  }
                  onChange={onOpenManageUnpaidLeave}
                />
              </Flex>
            </Flex>
          </Flex>

          <Flex gap="5px" flexDir="column" w="full">
            <Text color="brand.black" textStyle="extraSmallRegular">
              Leave date
            </Text>
            <Flex
              w="full"
              flexDir={{ base: 'column', md: 'row' }}
              justifyContent="start"
              alignItems={{ base: 'start', md: 'center' }}
              rowGap="20px"
              columnGap="10px"
            >
              <Text color="brand.black" textStyle="smallRegular" py="15px">
                {(() => {
                  const displayStatus =
                    individual?.status === 'ACTIVE' && individual?.terminationDate
                      ? 'LEAVING'
                      : individual?.status;
                  if (
                    individual?.terminationDate &&
                    ['LEAVING', 'LEFT', 'RESTRICTED'].includes(displayStatus || '')
                  ) {
                    return (
                      <Badge
                        colorScheme={
                          displayStatus === 'LEAVING' || displayStatus === 'RESTRICTED'
                            ? 'badge.leaving'
                            : 'badge.left'
                        }
                      >
                        {`${
                          displayStatus === 'LEAVING' || displayStatus === 'RESTRICTED'
                            ? 'Leaving'
                            : 'Left'
                        } on ${format(
                          parseISO(individual?.terminationDate || ''),
                          DEFAULT_DATE_FORMAT
                        )}`}
                      </Badge>
                    );
                  }
                  return '—';
                })()}
              </Text>

              <Button
                variant="link"
                size="sm"
                leftIcon={<CalendarOutline boxSize="20px" />}
                ml={{ base: 0, md: 'auto' }}
                onClick={individual?.terminationDate ? onOpenEditTerminationDate : onOpenTerminate}
                isDisabled={individual?.status === 'LEFT'}
              >
                {individual?.terminationDate ? 'Manage leave date' : 'Set leave date'}
              </Button>
            </Flex>
          </Flex>

          <PreviousEmploymentSection previousIndividualId={individual?.previousIndividualId} />
        </ProfileSection>
      </GridItem>
    );

    const renderAslanCardDetails = () => {
      const isCardLocked =
        card?.cardStatus === 'LOCKED' && card.statusChangedReason !== 'USER_APP_FREEZE'; // TODO: This key will change do EMPLOYEE_FREEZE

      const isLockDisabled =
        !card || !['ACTIVATED', 'RISK', 'LOST', 'FRAUD'].includes(card?.cardStatus);

      const cardStatusValue = !card
        ? CardStatusValues.CREATED
        : CardStatusValues[card?.cardStatus as CardStatus];

      const LockIcon = isCardLocked ? UnlockOutline : LockOutline;
      const getIndividualPath =
        card?.statusChangedByRole === 'EMPLOYEE' ? getEmployeeProfilePath : getAdminProfilePath;

      return (
        <GridItem display="flex">
          <ProfileSection
            title="Aslan card"
            button={
              <Button
                variant="link"
                size="sm"
                leftIcon={<LockIcon boxSize="20px" />}
                onClick={onOpenManageCardLock}
                isDisabled={isLockDisabled && card?.cardStatus !== 'LOCKED'}
              >
                {isCardLocked ? 'Unlock the card' : 'Lock the card'}
              </Button>
            }
          >
            <Flex gap="5px" flexDir="column" w="full">
              <Text color="brand.black" textStyle="extraSmallRegular">
                Card status
              </Text>
              <Badge mr="auto" mt="15px" mb="5px" colorScheme={`badge.${cardStatusValue.color}`}>
                {cardStatusValue.value}
              </Badge>
            </Flex>

            <HStack spacing="20px" align="flex-start">
              <Flex gap="5px" flexDir="column" w="full">
                <ProfileField
                  label="Card locked by"
                  value={card?.cardStatus === 'LOCKED' ? card?.statusChangedByName : undefined}
                  link={
                    card?.statusChangedReason !== 'SYSTEM_LOCK' && card?.statusChangedBy
                      ? getIndividualPath(card?.statusChangedBy)
                      : undefined
                  }
                />
              </Flex>
              <Flex gap="5px" flexDir="column" w="full">
                <Text color="brand.black" textStyle="extraSmallRegular">
                  Reason
                </Text>
                {card?.statusChangedReason && card?.cardStatus === 'LOCKED' ? (
                  <Badge
                    mr="auto"
                    mt="15px"
                    mb="5px"
                    colorScheme={`badge.${
                      CardStatusChangedReasonValues[
                        card?.statusChangedReason as CardStatusChangedReason
                      ]?.color
                    }`}
                  >
                    {
                      CardStatusChangedReasonValues[
                        card?.statusChangedReason as CardStatusChangedReason
                      ]?.value
                    }
                  </Badge>
                ) : (
                  <Text
                    py="15px"
                    color="brand.black"
                    textStyle="smallMedium"
                    wordBreak="break-word"
                  >
                    -
                  </Text>
                )}
              </Flex>
            </HStack>

            <HStack spacing="20px" align="flex-start">
              <ProfileField
                label="Tribe card ID"
                value={card?.externalCardId}
                {...(card?.externalCardId
                  ? { externalLink: getTribeAdminLink(card?.externalCardId, 'card') }
                  : {})}
              />
              <ProfileField
                label="Tribe wallet ID"
                value={wallet?.externalAccountId}
                {...(wallet?.externalAccountId
                  ? { externalLink: getTribeAdminLink(wallet?.externalAccountId, 'account') }
                  : {})}
              />
              <ProfileField
                label="Tribe holder ID"
                value={individual?.externalHolderId}
                {...(individual?.externalHolderId
                  ? {
                      externalLink: getTribeAdminLink(individual?.externalHolderId, 'holder'),
                    }
                  : {})}
              />
            </HStack>
          </ProfileSection>
        </GridItem>
      );
    };

    const renderOtherDetails = () => (
      <GridItem display="flex">
        <ProfileSection title="Other details">
          <HStack spacing="20px" align="flex-start">
            <ProfileField label="Aslan Internal ID" value={individual?.id} copyToClipboard />
            <ProfileField label="Referral Code" value={individual?.referralCode} />
            {isEmployee && (
              <ProfileField label="Payroll ID" value={individual?.externalPayrollId} />
            )}
          </HStack>
          {isEmployee && <ProfileField label="Internal wallet ID" value={wallet?.id} />}
          <HStack spacing="20px" align="flex-start">
            <ProfileField
              label="First invitation date"
              value={
                individual?.invitedAt &&
                format(parseISO(individual?.invitedAt), DEFAULT_DATE_FORMAT)
              }
            />
            <ProfileField
              label="Join date"
              value={
                individual?.joinDate && format(parseISO(individual?.joinDate), DEFAULT_DATE_FORMAT)
              }
            />
          </HStack>
        </ProfileSection>
      </GridItem>
    );

    return (
      <>
        <Grid
          w="full"
          templateColumns={{
            base: 'repeat(1, minmax(0px, 492px))',
            md: 'repeat(2, minmax(0px, 492px))',
          }}
          gap="25px"
        >
          {isEmployee && renderModuleDetails()}
          {renderGeneralDetails()}
          {isEmployee && renderEmployeeDetails(companyConfiguration)}
          {isEmployee && renderAslanCardDetails()}
          {renderOtherDetails()}
        </Grid>
        {renderModals()}
      </>
    );
  }
);

IndividualProfileTab.displayName = 'IndividualProfileTab';

export default IndividualProfileTab;
